/**
 * AuthProvider Component
 * ======================
 * Authentication context provider for the application.
 * Manages authentication state using sessionStorage.
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { AUTH_CONFIG } from '../../utils/constants';

/**
 * Authentication Context
 * @type {React.Context}
 */
export const AuthContext = createContext(null);

/**
 * AuthProvider Component
 * Provides authentication state and methods to child components.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AuthProvider component
 */
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  /**
   * Check authentication status on mount
   */
  useEffect(() => {
    const checkAuth = () => {
      try {
        const auth = sessionStorage.getItem(AUTH_CONFIG.STORAGE_KEY);
        const userData = sessionStorage.getItem(`${AUTH_CONFIG.STORAGE_KEY}_user`);
        
        if (auth === 'true') {
          setIsAuthenticated(true);
          if (userData) {
            setUser(JSON.parse(userData));
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  /**
   * Login function
   * Validates credentials and sets authentication state.
   * 
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {boolean} True if login successful
   */
  const login = useCallback((username, password) => {
    // Validate demo credentials
    if (username === AUTH_CONFIG.DEMO_USERNAME && password === AUTH_CONFIG.DEMO_PASSWORD) {
      try {
        const userData = {
          username: AUTH_CONFIG.DEMO_USERNAME,
          name: 'Demo User',
          role: 'admin',
          loginTime: new Date().toISOString(),
        };
        
        sessionStorage.setItem(AUTH_CONFIG.STORAGE_KEY, 'true');
        sessionStorage.setItem(`${AUTH_CONFIG.STORAGE_KEY}_user`, JSON.stringify(userData));
        
        setIsAuthenticated(true);
        setUser(userData);
        
        return true;
      } catch (error) {
        console.error('Error during login:', error);
        return false;
      }
    }
    
    return false;
  }, []);
  
  /**
   * Logout function
   * Clears authentication state and sessionStorage.
   */
  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem(AUTH_CONFIG.STORAGE_KEY);
      sessionStorage.removeItem(`${AUTH_CONFIG.STORAGE_KEY}_user`);
      
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, []);
  
  /**
   * Update user data
   * @param {object} updates - User data updates
   */
  const updateUser = useCallback((updates) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      
      const updatedUser = { ...prevUser, ...updates };
      
      try {
        sessionStorage.setItem(
          `${AUTH_CONFIG.STORAGE_KEY}_user`,
          JSON.stringify(updatedUser)
        );
      } catch (error) {
        console.error('Error updating user data:', error);
      }
      
      return updatedUser;
    });
  }, []);
  
  // Context value
  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    updateUser,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
